/**
 * Lead-room system (Sprint 3a): each fake room periodically posts a "call",
 * then that call resolves as HIT/MISS by the room's HIDDEN accuracy. Collusion
 * rooms can spring a dump trap (a bullish "get in now" that resolves down).
 * Resolved calls drop into a small per-room history (max 5) the player reads
 * to learn the room. Also trims expired info-dealer hints.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import type { LeadRoomState, RoomCall, RoomRuntime } from '../leadRoomState.js';
import { getCoinConfig } from '../config/coins.js';
import { listedCoinIds } from '../exchangeState.js';
import {
  LEAD_ROOMS, LEAD_ROOM_CONFIG, getLeadRoom,
  callHits, isTrapCall, resolveCallOutcome,
} from '../config/leadRooms.js';

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Create a fresh call for a room targeting a currently-listed coin (pure aside from Math.random). */
export function makeCall(roomId: string, now: number, listedIds: string[]): RoomCall {
  // collusion rooms lean toward meme coins (where whales operate)
  const cfg = getLeadRoom(roomId);
  const memeIds = listedIds.filter(id => getCoinConfig(id)?.tier === 'meme');
  const pool = (cfg?.personality === 'collusion' && memeIds.length > 0) ? memeIds : listedIds;
  const coinId = pool[Math.floor(Math.random() * pool.length)];
  const claimedDirection: 1 | -1 = Math.random() < 0.7 ? 1 : -1; // rooms mostly hype longs
  return {
    id: `${roomId}_${now}`,
    coinId,
    claimedDirection,
    variant: 1 + Math.floor(Math.random() * LEAD_ROOM_CONFIG.callVariants),
    createdAt: now,
    resolveAt: now + LEAD_ROOM_CONFIG.callLifetime,
    status: 'live',
  };
}

/** Resolve a live call by the room's hidden accuracy + trap logic (pure). */
export function resolveCall(roomId: string, call: RoomCall): RoomCall {
  const cfg = getLeadRoom(roomId);
  if (!cfg) return { ...call, status: 'miss', actualDirection: -call.claimedDirection as 1 | -1 };
  const trap = isTrapCall(cfg, call.claimedDirection, Math.random());
  const hit = trap ? false : callHits(cfg.accuracy, Math.random());
  const { actualDirection, success } = resolveCallOutcome(call.claimedDirection, hit, trap);
  return { ...call, status: success ? 'hit' : 'miss', actualDirection, trap };
}

/** Advance one room's runtime: resolve a due call, queue the next one (pure). */
export function tickRoom(roomId: string, room: RoomRuntime, now: number, listedIds: string[]): RoomRuntime {
  let next = room;

  // resolve a due live call -> push to history (newest first, capped)
  if (next.activeCall && now >= next.activeCall.resolveAt) {
    const resolved = resolveCall(roomId, next.activeCall);
    next = {
      ...next,
      activeCall: null,
      history: [resolved, ...next.history].slice(0, LEAD_ROOM_CONFIG.maxHistory),
      nextCallAt: now + randRange(LEAD_ROOM_CONFIG.callIntervalMin, LEAD_ROOM_CONFIG.callIntervalMax),
    };
  }

  // post a new call when due and none is live (needs a listed coin to target)
  if (!next.activeCall && now >= next.nextCallAt && listedIds.length > 0) {
    next = { ...next, activeCall: makeCall(roomId, now, listedIds) };
  }
  return next;
}

/** Drop expired info-dealer hints (pure). */
export function trimHints(lead: LeadRoomState, now: number): LeadRoomState {
  if (!lead.hints.some(h => now >= h.expiresAt)) return lead;
  return { ...lead, hints: lead.hints.filter(h => now < h.expiresAt) };
}

export const leadRoomSystem: TickSystem<GameState, GameAction> = {
  name: 'leadRooms',

  update(state) {
    if (!state.isRunning || !state.leadRooms) return state;
    const now = Date.now();
    const lead = state.leadRooms;
    const listedIds = state.exchange ? listedCoinIds(state.exchange) : [];

    let changed = false;
    const rooms: Record<string, RoomRuntime> = {};
    for (const cfg of LEAD_ROOMS) {
      const room = lead.rooms[cfg.id];
      if (!room) { rooms[cfg.id] = room; continue; }
      const nextRoom = tickRoom(cfg.id, room, now, listedIds);
      if (nextRoom !== room) changed = true;
      rooms[cfg.id] = nextRoom;
    }

    const trimmed = trimHints(changed ? { ...lead, rooms } : lead, now);
    if (!changed && trimmed === lead) return state;
    return { ...state, leadRooms: trimmed };
  },
};
